import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { ChangeEventHandler, memo, useCallback, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Button } from '../../components/Button';
import { ALL_TIME, DateRange, DateRangePicker, inRange } from '../../components/DateRangePicker';
import { FilterChips, FilterGroup } from '../../components/FilterChips';
import { Input } from '../../components/inputs/Input';
import { EarthLoader } from '../../components/loaders/EarthLoader';
import { PageContent } from '../../components/page/PageContent';
import { PageHeader } from '../../components/page/PageHeader';
import { useSubscriptionTypeTranslations } from '../../hooks/translations/useSubscriptionTypeTranslations';
import { isPartnerRole } from '../../modules/organisation/referrals/partnerRoles';
import { SalesHistory } from '../../modules/organisation/referrals/SalesHistory';
import { PartnerTerms, usePartnerTerms } from '../../modules/organisation/referrals/usePartnerTerms';
import { T, useT } from '../../translation/src';
import { CreditLedgerEntryType, FeePricingKind, ReferralDealKind, ReferralSaleFieldsFragment, ReferralSaleState, useOrganisationReferralOverviewQuery } from '../../types/graphqlTypes';
import { formatPrice } from '../../utils/formatPrice';

type Sale = ReferralSaleFieldsFragment;

// What a sale sold, as the "sold" filter sorts it: projects, or the kind of subscription fee.
const PROJECTS = 'PROJECTS';
const kindOf = (sale: Sale): string => (sale.ledgerEntryType === CreditLedgerEntryType.Grant ? PROJECTS : (sale.pricingKind ?? FeePricingKind.Unchanged));

// Where a sale stands for the partner: an order not invoiced yet, invoiced (or sold to a customer Pythagoras
// invoiced) but not paid, or paid.
enum SaleStatus {
  NotInvoiced = 'NOT_INVOICED',
  AwaitingPayment = 'AWAITING_PAYMENT',
  Paid = 'PAID',
}
const statusOf = (sale: Sale): SaleStatus => {
  if (sale.state === ReferralSaleState.Payable || sale.state === ReferralSaleState.PaidOut) return SaleStatus.Paid;
  if (sale.deductedOnInvoice && !sale.odooInvoiceId) return SaleStatus.NotInvoiced;
  return SaleStatus.AwaitingPayment;
};

type SalesFilters = {
  customer?: string[];
  kind?: string[];
  plan?: string[];
  deal?: ReferralDealKind[];
  status?: SaleStatus[];
};
const NO_FILTERS: SalesFilters = {};

// The words for what a sale sold, whether it was the customer's first, and its status.
const useSaleLabels = (terms: PartnerTerms): Record<string, string> => {
  const projects = useT('extra projects', { swc: true });
  const newSubscription = useT('new subscription', { swc: true });
  const upgrade = useT('upgrade', { swc: true });
  const renewal = useT('renewal', { swc: true });
  const nextTerm = useT('next term', { swc: true });
  const termChange = useT('term change', { swc: true });
  const recurring = useT('recurring', { swc: true });
  const notInvoiced = useT('not invoiced yet', { swc: true });
  const awaitingPayment = useT('awaiting payment', { swc: true });
  const paid = useT('paid', { swc: true });
  return useMemo(
    () => ({
      [PROJECTS]: projects,
      [FeePricingKind.NewSubscription]: newSubscription,
      [FeePricingKind.Upgrade]: upgrade,
      [FeePricingKind.Renewal]: renewal,
      [FeePricingKind.ScheduledDowngrade]: nextTerm,
      [FeePricingKind.Unchanged]: termChange,
      [ReferralDealKind.First]: terms.firstDeal,
      [ReferralDealKind.Recurring]: recurring,
      [SaleStatus.NotInvoiced]: notInvoiced,
      [SaleStatus.AwaitingPayment]: awaitingPayment,
      [SaleStatus.Paid]: paid,
    }),
    [projects, newSubscription, upgrade, renewal, nextTerm, termChange, terms.firstDeal, recurring, notInvoiced, awaitingPayment, paid],
  );
};

const useFilterGroups = (sales: Sale[], terms: PartnerTerms): FilterGroup<SalesFilters>[] => {
  const plans = useSubscriptionTypeTranslations();
  const labels = useSaleLabels(terms);
  const customer = useT('customer', { swc: true });
  const sold = terms.dealt;
  const plan = useT('plan', { swc: true });
  const deal = useT('first or recurring', { swc: true });
  const status = useT('status', { swc: true });

  return useMemo(() => {
    // Only what the sales hold: no filter option that matches nothing.
    const distinct = <Value extends string>(of: (sale: Sale) => Value | null | undefined) => [...new Set(sales.map(of).filter((value): value is Value => !!value))];
    const customers = new Map(sales.map((sale) => [sale.referredOrganisationId, sale.referredOrganisationName]));
    return [
      { key: 'customer', title: customer, options: [...customers.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([value, label]) => ({ value, label })) },
      { key: 'kind', title: sold, options: distinct(kindOf).map((value) => ({ value, label: labels[value] ?? value })) },
      { key: 'plan', title: plan, options: distinct((sale) => sale.subscriptionType).map((value) => ({ value, label: plans[value] })) },
      { key: 'deal', title: deal, options: distinct((sale) => sale.dealKind).map((value) => ({ value, label: labels[value] ?? value })) },
      { key: 'status', title: status, options: distinct(statusOf).map((value) => ({ value, label: labels[value] ?? value })) },
    ];
  }, [sales, customer, sold, plan, deal, status, plans, labels]);
};

// Each filter with values keeps the sales matching any of them; the filters and the period narrow each other.
const matches = (sale: Sale, filters: SalesFilters, search: string, period: DateRange): boolean =>
  inRange(sale.soldAt, period) &&
  (!filters.customer?.length || filters.customer.includes(sale.referredOrganisationId)) &&
  (!filters.kind?.length || filters.kind.includes(kindOf(sale))) &&
  (!filters.plan?.length || (!!sale.subscriptionType && filters.plan.includes(sale.subscriptionType))) &&
  (!filters.deal?.length || filters.deal.includes(sale.dealKind)) &&
  (!filters.status?.length || filters.status.includes(statusOf(sale))) &&
  (!search || sale.referredOrganisationName.toLowerCase().includes(search));

const Tile: React.FC<{ value: React.ReactNode; label: React.ReactNode }> = ({ value, label }) => (
  <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-[#242424]">
    <span className="text-2xl font-bold leading-tight">{value}</span>
    <span className="text-xs uppercase tracking-wide text-gray-500">{label}</span>
  </div>
);

const sum = (sales: Sale[], of: (sale: Sale) => number) => sales.reduce((total, sale) => total + of(sale), 0);

// The filtered orders or sales per customer: how many, their price, the partner's discount or commission on it, and
// (orders) what the partner pays.
const PerCustomer: React.FC<{ sales: Sale[]; terms: PartnerTerms }> = ({ sales, terms }) => {
  const rows = useMemo(() => {
    const byCustomer = new Map<string, { name: string; sales: Sale[] }>();
    for (const sale of sales) byCustomer.set(sale.referredOrganisationId, { name: sale.referredOrganisationName, sales: [...(byCustomer.get(sale.referredOrganisationId)?.sales ?? []), sale] });
    return [...byCustomer.entries()]
      .map(([id, { name, sales: own }]) => ({ id, name, count: own.length, amountCents: sum(own, (sale) => sale.amountCents), cutCents: sum(own, (sale) => sale.cutCents) }))
      .sort((a, b) => b.amountCents - a.amountCents);
  }, [sales]);
  return (
    <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#242424]">
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-6 py-3 font-medium">
              <T _str="customer" swc />
            </th>
            <th className="px-2 py-3 text-right font-medium">{terms.deals}</th>
            <th className="px-2 py-3 text-right font-medium">
              <T _str="price" swc />
            </th>
            <th className={classNames('py-3 text-right font-medium', terms.orders ? 'px-2' : 'px-6')}>{terms.share}</th>
            {terms.orders && <th className="px-6 py-3 text-right font-medium">{terms.yourShare}</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="px-6 py-2">{row.name}</td>
              <td className="px-2 py-2 text-right">{row.count}</td>
              <td className="px-2 py-2 text-right">{formatPrice(row.amountCents)}</td>
              <td className={classNames('py-2 text-right', terms.orders ? 'px-2' : 'px-6')}>{formatPrice(row.cutCents)}</td>
              {terms.orders && <td className="px-6 py-2 text-right font-semibold">{formatPrice(row.amountCents - row.cutCents)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// The filtered orders or sales as a CSV file, for the partner's own bookkeeping: an order with its discount and the
// partner's price, a sale with its commission.
const downloadCsv = (sales: Sale[], filename: string, orders: boolean) => {
  const quote = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;
  const header = orders
    ? ['date', 'customer', 'ordered', 'plan', 'projects', 'first or recurring', 'price (EUR)', 'discount (%)', 'discount (EUR)', 'your price (EUR)', 'status', 'invoice']
    : ['date', 'customer', 'sold', 'plan', 'projects', 'first or recurring', 'price (EUR)', 'commission (%)', 'commission (EUR)', 'status', 'invoice'];
  const lines = sales.map((sale) =>
    [
      new Date(sale.soldAt).toISOString().slice(0, 10),
      sale.referredOrganisationName,
      kindOf(sale),
      sale.subscriptionType ?? '',
      sale.credits ?? '',
      sale.dealKind,
      (sale.amountCents / 100).toFixed(2),
      (sale.cutBasisPoints / 100).toFixed(2),
      (sale.cutCents / 100).toFixed(2),
      ...(orders ? [((sale.amountCents - sale.cutCents) / 100).toFixed(2)] : []),
      statusOf(sale),
      sale.odooInvoiceId ?? '',
    ]
      .map(quote)
      .join(','),
  );
  const url = URL.createObjectURL(new Blob([[header.map(quote).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

type View = 'orders' | 'customers';

// A partner's orders (a reseller, a full-service partner) or sales (a lead generator) in full, filtered by customer,
// what was ordered or sold, plan, first or recurring, status and period (the admin center's filter button), with the
// totals of what is shown, a per-customer view and a CSV export: where the partner sees who bought what.
const Sales_: React.FC2 = () => {
  const { organisationId = '' } = useParams();
  const searchPlaceholder = useT('search customer', { swc: true });
  const { data, loading } = useOrganisationReferralOverviewQuery({ variables: { organisationId }, fetchPolicy: 'cache-and-network' });
  const overview = data?.organisationReferralOverview;
  const sales = useMemo(() => overview?.sales ?? [], [overview?.sales]);
  const terms = usePartnerTerms(overview?.role);
  const groups = useFilterGroups(sales, terms);
  const [filters, setFilters] = useState<SalesFilters>(NO_FILTERS);
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState<DateRange>(ALL_TIME);
  const [view, setView] = useState<View>('orders');
  const onChangeSearch: ChangeEventHandler<HTMLInputElement> = useCallback((event) => setSearch(event.target.value), []);
  const term = search.trim().toLowerCase();
  const shown = useMemo(() => sales.filter((sale) => matches(sale, filters, term, period)), [filters, period, sales, term]);
  const amountCents = sum(shown, (sale) => sale.amountCents);
  const cutCents = sum(shown, (sale) => sale.cutCents);
  // Named after the period, for the accountant's folder.
  const onExport = useCallback(
    () => downloadCsv(shown, `${terms.orders ? 'orders' : 'sales'}-${period.from || period.to ? `${period.from ?? 'start'}_${period.to ?? 'now'}` : new Date().toISOString().slice(0, 10)}.csv`, terms.orders),
    [period, shown, terms.orders],
  );

  // Only a partner has orders or sales: anyone else is sent back to the referral program page.
  if (overview && !isPartnerRole(overview.role)) return <Navigate to={`/organisations/${organisationId}/referrals`} replace />;

  return (
    <PageContent>
      <PageHeader title={<span>{terms.deals}</span>} />
      <div className="w-full min-h-0 flex-1 overflow-y-auto">
        {loading && !overview ? (
          <EarthLoader />
        ) : (
          <div className="flex flex-col w-full gap-4 pb-10">
            <div className="flex flex-wrap items-center gap-3">
              <Input className="w-52" placeholder={`${searchPlaceholder}...`} value={search} onChange={onChangeSearch} />
              <DateRangePicker value={period} onChange={setPeriod} />
              <FilterChips groups={groups} value={filters} onChange={setFilters} defaults={NO_FILTERS} />
              <div className="ml-auto flex items-center gap-2">
                <div className="flex rounded-full border border-gray-300 p-0.5 text-sm dark:border-gray-600">
                  {(['orders', 'customers'] as const).map((one) => (
                    <button key={one} type="button" onClick={() => setView(one)} className={classNames('rounded-full px-3 py-1', view === one ? 'bg-neon-green-300 font-semibold text-eerie-black' : 'text-gray-600 dark:text-gray-300')}>
                      {one === 'orders' ? terms.deals : <T _str="per customer" swc />}
                    </button>
                  ))}
                </div>
                <Button variant="secondary" size="sm" onClick={onExport} disabled={!shown.length}>
                  <ArrowDownTrayIcon className="mr-1 h-4 w-4" />
                  CSV
                </Button>
              </div>
            </div>
            <div className={classNames('grid grid-cols-2 gap-4', terms.orders ? 'lg:grid-cols-4' : 'lg:grid-cols-3')}>
              <Tile value={shown.length} label={terms.deals} />
              <Tile value={formatPrice(amountCents)} label={<T _str="price" swc />} />
              <Tile value={formatPrice(cutCents)} label={terms.orders ? terms.share : terms.yourShare} />
              {terms.orders && <Tile value={formatPrice(amountCents - cutCents)} label={terms.yourShare} />}
            </div>
            {overview && (view === 'orders' ? <SalesHistory sales={shown} role={overview.role} fullHeight /> : <PerCustomer sales={shown} terms={terms} />)}
          </div>
        )}
      </div>
    </PageContent>
  );
};
export const Sales = memo(Sales_);
